/**
 * Camila settings — kill switch toggle.
 *
 * Allows toggling Camila on/off globally and per channel from the admin
 * panel. Changes take effect within 30 seconds (in-memory cache TTL)
 * without restarting the API.
 *
 * The env var CAMILA_ENABLED is the hard override: if env says off,
 * the DB toggle cannot turn it on.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { apiFetch } from '@/lib/query-client';

export const Route = createFileRoute('/_app/settings/camila')({
  component: CamilaSettingsPage,
});

interface CamilaFlagResponse {
  effective: { enabled: boolean; channels: string[] };
  envOverride: { enabled: boolean; channels: string[] };
  db: { enabled: boolean; channels: string[]; updatedAt: string; updatedBy: string | null } | null;
}

interface UpdateResponse {
  effective: { enabled: boolean; channels: string[] };
  db: { enabled: boolean; channels: string[]; updatedAt: string; updatedBy: string | null };
  envOverrideActive: boolean;
}

const ALL_CHANNELS = ['whatsapp', 'instagram', 'messenger'] as const;

function CamilaSettingsPage() {
  const [data, setData] = useState<CamilaFlagResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Local toggle state (mirrors DB, not effective)
  const [enabled, setEnabled] = useState(false);
  const [channels, setChannels] = useState<Set<string>>(new Set(ALL_CHANNELS));

  const fetchData = useCallback(async () => {
    try {
      const res = await apiFetch<CamilaFlagResponse>({
        path: '/v1/feature-flags/camila',
        method: 'GET',
      });
      setData(res);
      // Initialize local state from DB (or defaults if no row)
      if (res.db) {
        setEnabled(res.db.enabled);
        setChannels(new Set(res.db.channels));
      } else {
        setEnabled(false);
        setChannels(new Set(ALL_CHANNELS));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await apiFetch<UpdateResponse>({
        path: '/v1/feature-flags/camila',
        method: 'PUT',
        body: JSON.stringify({ enabled, channels: [...channels] }),
        headers: { 'Content-Type': 'application/json' },
      });

      // Refresh full data
      await fetchData();

      if (res.envOverrideActive) {
        setError('Camila esta apagada por variable de entorno. El toggle de base de datos no puede encenderla.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleChannel = (ch: string) => {
    setChannels((prev) => {
      const next = new Set(prev);
      if (next.has(ch)) {
        next.delete(ch);
      } else {
        next.add(ch);
      }
      return next;
    });
  };

  const envOff = data && !data.envOverride.enabled;

  if (loading) {
    return (
      <div className="mx-auto max-w-xl p-6">
        <p className="text-sm text-[color:var(--color-fg-subtle)]">Cargando...</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl space-y-6 p-6">
      <div>
        <h1 className="text-xl font-semibold text-[color:var(--color-fg)]">Camila</h1>
        <p className="mt-1 text-sm text-[color:var(--color-fg-subtle)]">
          Agente conversacional. Los cambios toman efecto en menos de 30 segundos.
        </p>
      </div>

      {/* Env override warning */}
      {envOff && (
        <div className="rounded-md border border-[color:var(--color-warning)] bg-amber-50 p-3 text-sm text-amber-800">
          La variable de entorno <code className="font-mono">CAMILA_ENABLED</code> esta vacia o en{' '}
          <code className="font-mono">false</code>. Camila permanece apagada independientemente del
          toggle de base de datos. Para habilitar, setea{' '}
          <code className="font-mono">CAMILA_ENABLED=true</code> en el servidor y reinicia.
        </div>
      )}

      {/* Main toggle */}
      <div className="space-y-4 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-4">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-base font-medium">Camila activa</Label>
            <p className="text-xs text-[color:var(--color-fg-subtle)]">
              Interruptor global. Si esta apagado, Camila no responde en ningun canal.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            onClick={() => setEnabled(!enabled)}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
              enabled
                ? 'bg-[color:var(--color-primary)]'
                : 'bg-[color:var(--color-bg-overlay)]'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
                enabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {/* Channel toggles */}
        {enabled && (
          <div className="space-y-2 border-t border-[color:var(--color-border)] pt-3">
            <Label className="text-sm font-medium">Canales habilitados</Label>
            <div className="flex flex-col gap-2">
              {ALL_CHANNELS.map((ch) => (
                <label key={ch} className="flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={channels.has(ch)}
                    onChange={() => toggleChannel(ch)}
                    className="h-4 w-4 cursor-pointer rounded border-[color:var(--color-border)]"
                  />
                  <span className="text-[color:var(--color-fg)] capitalize">{ch}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Effective status */}
      {data && (
        <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] p-3 text-sm">
          <p className="font-medium text-[color:var(--color-fg)]">Estado efectivo</p>
          <p className="mt-1 text-[color:var(--color-fg-subtle)]">
            {data.effective.enabled ? (
              <>
                Activa en:{' '}
                <span className="font-medium text-[color:var(--color-fg)]">
                  {data.effective.channels.join(', ') || 'ningun canal'}
                </span>
              </>
            ) : (
              <span className="font-medium text-[color:var(--color-danger)]">Apagada</span>
            )}
          </p>
          {data.db?.updatedAt && (
            <p className="mt-1 text-xs text-[color:var(--color-fg-subtle)]">
              Ultima modificacion: {new Date(data.db.updatedAt).toLocaleString('es-CL')}
            </p>
          )}
        </div>
      )}

      {/* Save */}
      <div className="flex items-center gap-3">
        <Button type="button" variant="primary" disabled={saving} onClick={handleSave}>
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
        {error && (
          <span className="text-sm text-[color:var(--color-danger)]">{error}</span>
        )}
      </div>
    </div>
  );
}
